import { App } from "@capacitor/app";

/**
 * Custom URL scheme registered in iOS Info.plist and Android AndroidManifest.xml.
 * Used to open the native app from mobile browsers where Universal Links don't fire.
 */
export const APP_CUSTOM_SCHEME = "clipboardstaffing.facility";

import {
  clearDeepLinkHandler,
  isDuplicate,
  markHandled,
  setDeepLinkHandler,
} from "./deepLinkQueue";
import { Platform } from "./platform";

/**
 * Normalise a deep link URL to a router-compatible path.
 *
 * Strips the custom URL scheme, preserving the full pathname and search string
 * so that deep links like `clipboardstaffing.facility:///login/verify?authUrl=...`
 * are passed through to the router as `/login/verify?authUrl=...`.
 */
export function normaliseDeepLink(rawUrl: string): string {
  const urlObject = new URL(rawUrl);
  return `${urlObject.pathname}${urlObject.search}` || "/";
}

/**
 * Initialize deep link handling for Capacitor mobile app.
 *
 * Consumes from the module-scoped deep link queue (`deepLinkQueue.ts`) which
 * captures `appUrlOpen` events at import time — including cold-start events
 * that fire before React mounts. Also checks for a launch URL on init.
 *
 * Deep links use a custom URL scheme: clipboardstaffing.facility:///path?params
 * Example: clipboardstaffing.facility:///login/verify?authUrl=...
 */
export function initializeDeepLinkHandler(onDeepLink: (url: string) => void): () => void {
  if (!Platform.isMobile) {
    return () => {
      // No-op for web
    };
  }

  void (async () => {
    // Check if app was launched via deep link (getLaunchUrl is separate
    // from the appUrlOpen event stream and must be checked explicitly).
    const launchUrl = await App.getLaunchUrl();
    if (launchUrl?.url && !isDuplicate(launchUrl.url)) {
      markHandled(launchUrl.url);
      onDeepLink(normaliseDeepLink(String(launchUrl.url)));
    }
  })();

  // Wire up as the consumer of the module-scoped deep link queue.
  // This drains any URLs that arrived before this handler was ready
  // and routes future appUrlOpen events directly here.
  setDeepLinkHandler((data) => {
    onDeepLink(normaliseDeepLink(data.url));
  });

  return () => {
    clearDeepLinkHandler();
  };
}
